import { useMemo } from 'react';
import { buildQuickOrderColumns } from '../../components/QuickOrderTable/QuickOrderTable.columns';
import {
  getQuickOrderCurrentItem,
  getSelectedLocationSummary,
  getSelectedLocationTotal,
} from '../../components/QuickOrderTable/quickOrderTableUtils';

const useQuickOrderTableModel = ({ quickOrderData, vendor }) => {
  const { settings, slug } = vendor;
  const { batchQueuesEnabled, displayLiveInventoryCount } = settings;
  const { items, selectedLocations } = quickOrderData;

  const sortedSelectedLocations = useMemo(
    () => [...selectedLocations].sort((a, b) => a.name.localeCompare(b.name)),
    [selectedLocations]
  );

  const tableColumns = useMemo(
    () =>
      buildQuickOrderColumns({
        batchQueuesEnabled,
        displayLiveInventoryCount,
        selectedLocations: sortedSelectedLocations,
        slug,
      }),
    [
      batchQueuesEnabled,
      displayLiveInventoryCount,
      sortedSelectedLocations,
      slug,
    ]
  );

  const tableData = useMemo(
    () =>
      items.map((item) => {
        const currentItem = getQuickOrderCurrentItem({
          batchQueuesEnabled,
          item,
        });
        const summary = getSelectedLocationSummary({
          currentItem,
          selectedLocations,
        });
        const totalQty = (currentItem.locations || []).reduce(
          (sum, loc) => sum + Number(loc.quantity || 0),
          0
        );

        return {
          ...item,
          selectedLocationCount: summary.selectedLocationCount,
          selectedLocationTotal: getSelectedLocationTotal({
            batchQueuesEnabled,
            currentItem,
            item,
            selectedLocations,
            selectedLocationSummary: summary,
            totalQty,
          }),
        };
      }),
    [batchQueuesEnabled, items, selectedLocations]
  );

  const totals = useMemo(
    () =>
      tableData.reduce(
        (summary, item) => ({
          totalItems: summary.totalItems + item.selectedLocationCount,
          totalPrice: summary.totalPrice + item.selectedLocationTotal,
        }),
        {
          totalItems: 0,
          totalPrice: 0,
        }
      ),
    [tableData]
  );

  return {
    tableColumns,
    tableData,
    totals,
  };
};

export default useQuickOrderTableModel;
